/**
 * @fileoverview The i18next NAMESPACE DESCRIPTOR — shape, validation, and locale resolution.
 * @package @puffin-core/i18n-bootstrap
 *
 * 🔴 **Why the CONTRACT lives here and the REGISTRIES live in `@puffin-core/i18n-namespaces`.**
 * Putting these four pure functions in the composition package would have created a PACKAGE-LEVEL
 * CYCLE — `i18n-bootstrap → i18n-namespaces → page-shell → i18n-bootstrap` — which is precisely the
 * shape I18N-BOOTSTRAP-DEPCYCLE-001 exists to forbid. It would have been acyclic at the MODULE
 * level (this file imports nothing), and that distinction is exactly the sort of subtlety a
 * bundler, a hoist or a future refactor stops honouring.
 *
 * So the split is by DEPENDENCY WEIGHT, not by topic: the foundational package owns the contract
 * (pure, import-free, consumed by the factory), and the composition package owns the instances
 * (which need the four UI packages). Direction stays one-way:
 *
 *   app → i18n-namespaces → { page-shell · login-shell · onboarding-ui · captcha-ui } → i18n-bootstrap
 *
 * AAS-I18N-002 ruling 39 (2026-08-10). A descriptor is the whole interface between a package that
 * ships translated chrome and the factory that boots an app's i18next instance:
 *
 *   { namespace: 'shell', supportedLocales: ['en','es','en-XA'], register(i18n, locales) }
 *
 * ## Why a descriptor at all
 *
 * Before this, `createAppI18n` took `loadShellStrings` + `shellLocales` + `loadLoginStrings` +
 * `loginLocales` as four separate parameters, each app wired 1–4 of them BY HAND across **20 call
 * sites**, and `@puffin-core/captcha-ui` gave up and registered itself from a React RENDER. Four
 * packages, three different mechanisms, and the repo's own doctrine — *factory-first, no rule of
 * three* — forbids exactly that.
 *
 * The user's ruling names the failure precisely: *"Every one of my options accepts that namespace
 * registration is something an APP must remember."* A descriptor moves the knowledge to where it
 * belongs — **the package that owns the strings declares which locales it can serve**, and the
 * factory does the rest. `createAppI18n` no longer contains a hardcoded
 * `PAGE_SHELL_SUPPORTED = new Set(['en','es','en-XA'])`, which was a foundational package holding a
 * stale copy of a UI package's private fact.
 *
 * ## DEPCYCLE-001 is preserved, deliberately
 *
 * `@puffin-core/i18n-bootstrap` must NEVER import `@puffin-core/page-shell` — that inverted import
 * was a module-eval cycle that resolved `loadShellStrings` to `undefined` and crashed every
 * consumer mounting a page-shell surface. So registration is still **injected**. What changes is the
 * ARITY: injected ONCE, from one composition module, instead of twenty times by hand.
 *
 * @module @puffin-core/i18n-bootstrap/namespaceRegistry
 */

/**
 * Validate and freeze one namespace descriptor.
 *
 * 🔴 **This throws, and the asymmetry with the old behaviour is the point.** `createAppI18n` used to
 * throw when `loadShellStrings` was missing but SILENTLY SKIP a missing `loadLoginStrings` — and that
 * silence is exactly how ten apps shipped a login screen that rendered English in every locale for
 * months. With a registry there is no "missing loader" case left to be lenient about, so the only
 * remaining failure is a MALFORMED descriptor, which is a programming error and must be loud.
 *
 * @param {Object} descriptor
 * @param {string} descriptor.namespace - The i18next namespace key, e.g. `'shell'`.
 * @param {string[]} descriptor.supportedLocales - Locale codes this package ships bundles for.
 *   Sourced from the OWNING PACKAGE's exported constant, never restated here.
 * @param {(i18n: object, locales: string[]) => void} descriptor.register - The package's own loader.
 * @returns {Readonly<Object>} The frozen descriptor.
 * @throws {TypeError} On any malformed field, naming the field and the descriptor.
 */
export function defineNamespace({ namespace, supportedLocales, register } = {}) {
  if (typeof namespace !== 'string' || namespace.length === 0) {
    throw new TypeError('defineNamespace: `namespace` must be a non-empty string');
  }
  if (!Array.isArray(supportedLocales) || supportedLocales.length === 0) {
    throw new TypeError(
      `defineNamespace[${namespace}]: \`supportedLocales\` must be a non-empty array. An empty ` +
        'list would silently register nothing, which is the failure mode this registry exists to ' +
        'remove.',
    );
  }
  if (supportedLocales.some((c) => typeof c !== 'string' || c.length === 0)) {
    throw new TypeError(`defineNamespace[${namespace}]: every supported locale must be a string`);
  }
  if (typeof register !== 'function') {
    throw new TypeError(`defineNamespace[${namespace}]: \`register\` must be a function`);
  }
  return Object.freeze({
    namespace,
    supportedLocales: Object.freeze([...supportedLocales]),
    register,
  });
}

/**
 * Validate a whole registry: an array of descriptors with no duplicate namespace.
 *
 * A duplicate is worth catching by name rather than by symptom: two descriptors for one namespace
 * both call `addResourceBundle` with `overwrite=false`, so the SECOND one silently does nothing and
 * the app renders whichever bundle happened to be registered first.
 *
 * @param {Array<Object>} registry - Descriptors.
 * @param {string} label - Registry name, for the error message.
 * @returns {Readonly<Array<Object>>} The frozen registry.
 * @throws {TypeError} When not an array, or when a namespace repeats.
 */
export function defineNamespaceRegistry(registry, label) {
  if (!Array.isArray(registry)) {
    throw new TypeError(`${label}: a namespace registry must be an array of descriptors`);
  }
  const seen = new Set();
  for (const d of registry) {
    if (seen.has(d.namespace)) {
      throw new TypeError(
        `${label}: duplicate namespace "${d.namespace}". Two descriptors for one namespace means ` +
          'the second `addResourceBundle(…, overwrite=false)` is a silent no-op.',
      );
    }
    seen.add(d.namespace);
  }
  return Object.freeze([...registry]);
}

/**
 * Which of an app's locales this descriptor can actually serve.
 *
 * ⚠️ The intersection direction matters. Registering a locale the package has no bundle for makes
 * its loader THROW (`loadShellStrings: unsupported locale "fr"`), and registering only the app's
 * advertised languages leaves an unadvertised PSEUDO-LOCALE unregistered — which is how `en-XA`
 * rendered unmarked shared-package chrome and made "unmarked ⇒ hardcoded" a ~97%-false-positive
 * signal. So the requested set is `app resources ∪ pseudo-locales`, intersected with what the
 * package declares.
 *
 * @param {Object} descriptor - A validated descriptor.
 * @param {string[]} requestedLocales - App resource codes plus any pseudo-locale codes.
 * @returns {string[]} The locales to register, order-stable and de-duplicated.
 */
export function resolveNamespaceLocales(descriptor, requestedLocales) {
  const supported = new Set(descriptor.supportedLocales);
  const out = [];
  for (const code of requestedLocales) {
    if (supported.has(code) && !out.includes(code)) out.push(code);
  }
  return out;
}

/**
 * Register every descriptor in a registry onto one i18next instance.
 *
 * Called by `createAppI18n` immediately after `init()` and BEFORE first paint — which is what let
 * `@puffin-core/captcha-ui` drop its render-phase `ensureCaptchaStrings` side effect. That side
 * effect existed because a `useEffect` would have left the first paint English forever
 * (react-i18next's `useTranslation` binds `languageChanged`, not `added`); boot-time registration has
 * no such window.
 *
 * ✅ **The drop HAPPENED** (I18N-RENDER-002 ruling R-2, 2026-08-22) — this docblock described it in
 * the conditional until then. `ensureCaptchaStrings` is deleted, and the guarantee this function's
 * timing provides is now load-bearing rather than merely available:
 * `tests/cross-app/contracts/captchaRegistryCoverage.contract.test.js` fails if `captcha` leaves
 * either registry.
 *
 * @param {Object} i18n - The i18next instance.
 * @param {Array<Object>} registry - Validated descriptors.
 * @param {string[]} requestedLocales - App resource codes plus any pseudo-locale codes.
 * @returns {Array<{namespace: string, locales: string[]}>} What was registered — returned rather
 *   than logged so callers and tests can ASSERT on it instead of trusting a side effect.
 */
export function registerNamespaces(i18n, registry, requestedLocales) {
  const registered = [];
  for (const descriptor of registry) {
    const locales = resolveNamespaceLocales(descriptor, requestedLocales);
    if (locales.length === 0) continue;
    descriptor.register(i18n, locales);
    registered.push({ namespace: descriptor.namespace, locales });
  }
  return registered;
}
